/* eslint-disable */
import { extendTheme } from "@chakra-ui/react";

const theme = extendTheme({
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },
  colors: {
    // Primary Brand Colors
    brand: {
      // Primary blue variations
      primary: "var(--color-brand-primary)",
      primaryLight: "var(--color-brand-primary-light)",
      primaryLighter: "var(--color-brand-primary-lighter)",
      primaryDark: "var(--color-brand-primary-dark)",
      primaryDarker: "var(--color-brand-primary-darker)",
      primaryBorder: "var(--color-brand-primary-border)",
      primaryAccent: "var(--color-brand-primary-accent)",

      // Legacy naming (for backwards compatibility)
      blue: "var(--color-brand-primary)",
      lightBlue: "var(--color-brand-primary-light)",
      darkBlue: "var(--color-brand-primary-dark)",

      // Secondary
      secondary: "var(--color-brand-secondary)",
      sidebarBg: "var(--color-brand-sidebar-bg)",
    },

    // Extended Gray Scale
    gray: {
      50: "var(--color-gray-50)",
      100: "var(--color-gray-100)",
      200: "var(--color-gray-200)",
      300: "var(--color-gray-300)",
      400: "var(--color-gray-400)",
      500: "var(--color-gray-500)",
      600: "var(--color-gray-600)",
      700: "var(--color-gray-700)",
      800: "var(--color-gray-800)",
      900: "var(--color-gray-900)",
    },

    // UI Colors
    ui: {
      bgPage: "var(--color-bg-page)",
      bgCard: "var(--color-bg-card)",
      bgSidebar: "var(--color-bg-sidebar)",
      bgHover: "var(--color-bg-hover)",
      bgSelected: "var(--color-bg-selected)",
      bgInput: "var(--color-bg-input)",
      bgPanel: "var(--color-bg-panel)",

      borderLight: "var(--color-border-light)",
      borderMedium: "var(--color-border-medium)",
      borderDark: "var(--color-border-dark)",
      borderInput: "var(--color-border-input)",
    },

    // Status Colors
    success: {
      50: "var(--color-success-light)",
      500: "var(--color-success)",
      600: "var(--color-success-dark)",
    },

    error: {
      50: "var(--color-error-light)",
      200: "var(--color-error-border)",
      500: "var(--color-error)",
      600: "var(--color-error-dark)",
      700: "var(--color-error-text)",
    },

    warning: {
      50: "var(--color-warning-light)",
      500: "var(--color-warning)",
      600: "var(--color-warning-dark)",
    },

    info: {
      50: "var(--color-info-light)",
      500: "var(--color-info)",
      600: "var(--color-info-dark)",
    },

    // Text Colors
    text: {
      primary: "var(--color-text-primary)",
      secondary: "var(--color-text-secondary)",
      tertiary: "var(--color-text-tertiary)",
      disabled: "var(--color-text-disabled)",
      inverse: "var(--color-text-inverse)",
      link: "var(--color-text-link)",
      linkHover: "var(--color-text-link-hover)",
    },

    // Comment Highlights
    comment: {
      yellow: "var(--color-comment-yellow)",
      blue: "var(--color-comment-blue)",
      green: "var(--color-comment-green)",
      pink: "var(--color-comment-pink)",
    },
  },

  // Component Style Defaults
  components: {
    Button: {
      baseStyle: {
        fontWeight: "500",
        borderRadius: "md",
      },
      variants: {
        solid: {
          bg: "brand.primary",
          color: "white",
          _hover: {
            bg: "brand.primaryDark",
          },
          _active: {
            bg: "brand.primaryDarker",
          },
        },
        outline: {
          borderColor: "brand.primary",
          color: "brand.primary",
          _hover: {
            bg: "brand.50",
          },
        },
      },
      defaultProps: {
        colorScheme: "brand",
      },
    },

    Input: {
      variants: {
        outline: {
          field: {
            borderColor: "ui.borderInput",
            _focus: {
              borderColor: "brand.primaryBorder",
              boxShadow: "0 0 0 1px var(--chakra-colors-brand-primaryBorder)",
            },
          },
        },
      },
    },

    Link: {
      baseStyle: {
        color: "text.link",
        _hover: {
          color: "text.linkHover",
          textDecoration: "underline",
        },
      },
    },

    Tabs: {
      variants: {
        enclosed: {
          tab: {
            _selected: {
              color: "brand.primary",
              borderColor: "ui.borderMedium",
              borderBottomColor: "ui.bgPage",
            },
          },
          tablist: {
            borderBottomColor: "ui.borderMedium",
          },
        },
        "enclosed-colored": {
          tab: {
            _selected: {
              color: "brand.primary",
              bg: "ui.bgPage",
              borderColor: "ui.borderMedium",
              borderBottomColor: "ui.bgPage",
            },
          },
          tablist: {
            borderBottomColor: "ui.borderMedium",
          },
        },
        line: {
          tab: {
            _selected: {
              color: "brand.primary",
              borderColor: "brand.primary",
            },
          },
          tablist: {
            borderColor: "ui.borderMedium",
          },
        },
      },
    },
  },

  // Global Styles
  styles: {
    global: {
      body: {
        bg: "ui.bgPage",
        color: "text.primary",
      },
      a: {
        color: "text.link",
        _hover: {
          color: "text.linkHover",
        },
      },
    },
  },

  semanticTokens: {
    colors: {
      "chakra-body-bg": {
        default: "ui.bgPage",
      },
      "chakra-body-text": {
        default: "text.primary",
      },
    },
  },

  // Border Radius
  radii: {
    sm: "4px",
    md: "8px",
    lg: "10px",
    xl: "12px",
  },

  // Shadows
  shadows: {
    sm: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
    md: "0 4px 6px -1px rgba(16, 24, 40, 0.05), 0 2px 4px -1px rgba(16, 24, 40, 0.03)",
    lg: "0 10px 15px -3px rgba(16, 24, 40, 0.05), 0 4px 6px -2px rgba(16, 24, 40, 0.03)",
    xl: "0 20px 25px -5px rgba(16, 24, 40, 0.05), 0 10px 10px -5px rgba(16, 24, 40, 0.02)",
  },
});

export default theme;
